import { useCallback, useEffect, useRef, useState } from 'react';
import type { UserRolePermissions, UserRoleListItem } from '@/services/userRoleService';
import type { getUserRoleService } from '@/services/userRoleService';
import { ApiError } from '@/services/apiClient';
import { prefixKey } from '@/utils/storagePrefix';
import { mockUsers, type UserData } from '../mockRoleData';
import { buildPermissionStateFromData } from './permissionState';
import type { PermissionState } from './permissionState';
import { buildSnapshotFromData } from './permissionDiffHelpers';
import type { PermissionSnapshot } from './permissionDiffHelpers';

export type UserRoleServiceInstance = ReturnType<typeof getUserRoleService>;

export interface UseEditRoleDataOptions {
  roleId?: string;
  isCreateMode: boolean;
  userRoleService: UserRoleServiceInstance;
  setAllPermissions: (permissions: Partial<PermissionState>) => void;
}

/**
 * Loads the role, its permissions and its assigned users, and owns the
 * non-permission role fields shown on the Details tab.
 */
export const useEditRoleData = ({ roleId, isCreateMode, userRoleService, setAllPermissions }: UseEditRoleDataOptions) => {
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [isSystem, setIsSystem] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [users, setUsers] = useState<UserData[]>([]);
  const [allRoles, setAllRoles] = useState<UserRoleListItem[]>([]);
  const [accessDenied, setAccessDenied] = useState(false);

  // Snapshot of the permissions as loaded, used to compute the save diff
  const initialSnapshotRef = useRef<PermissionSnapshot | null>(null);

  const loadPermissions = useCallback(
    (permissionsData: UserRolePermissions, saveSnapshot = false) => {
      try {
        if (saveSnapshot) {
          initialSnapshotRef.current = buildSnapshotFromData(permissionsData);
        }
        setAllPermissions(buildPermissionStateFromData(permissionsData));
      } catch (error) {
        console.error('Error loading permissions from data:', error);
      }
    },
    [setAllPermissions],
  );

  useEffect(() => {
    const loadRoleData = async () => {
      try {
        const roles = await userRoleService.getAllUserRoles();
        setAllRoles(roles);
      } catch (error) {
        console.error('Error loading all roles:', error);
        if (error instanceof ApiError && (error.status === 403 || error.status === 401)) {
          setAccessDenied(true);
          return;
        }
      }

      if (!isCreateMode && roleId) {
        localStorage.setItem(prefixKey('currentEditingRoleId'), roleId);
        window.dispatchEvent(new Event('permissionsUpdated'));
      }

      if (!isCreateMode && roleId) {
        try {
          const role = await userRoleService.getUserRoleById(Number(roleId));
          setRoleName(role.name);
          setRoleDescription(role.description || '');
          setIsSystem(role.isSystem);
          setIsActive(role.isSystem ? true : role.isActive);
          if (role.permissions) loadPermissions(role.permissions, true);
          try {
            const employees = await userRoleService.getEmployeesByRoleId(Number(roleId));
            setUsers(employees.map(emp => ({
              id: String(emp.id),
              name: emp.name,
              email: emp.email,
              roleId: roleId,
              roleName: emp.roleName || role.name,
              status: emp.status === 'active' ? 'active' : 'inactive',
            })));
          } catch {
            setUsers(mockUsers.filter(u => u.roleId === roleId));
          }
        } catch (error) {
          console.error('Error loading role:', error);
          if (error instanceof ApiError && (error.status === 403 || error.status === 401)) {
            setAccessDenied(true);
            return;
          }
        }
      } else if (isCreateMode) {
        const duplicationDataStr = sessionStorage.getItem('roleDuplicationData');
        if (duplicationDataStr) {
          try {
            const duplicationData = JSON.parse(duplicationDataStr);
            const sourceRole = await userRoleService.getUserRoleById(duplicationData.sourceRoleId);
            if (sourceRole.permissions) loadPermissions(sourceRole.permissions);
            setRoleName(`${duplicationData.sourceRoleName} (Copy)`);
            setRoleDescription('');
            sessionStorage.removeItem('roleDuplicationData');
          } catch (error) {
            console.error('Error loading duplication data:', error);
          }
        } else {
          setRoleName('');
          setRoleDescription('');
          setAllPermissions({ enabledPermissions: new Set<string>() });
          setIsSystem(false);
          setIsActive(true);
          setUsers([]);
        }
      }
    };
    loadRoleData();
  }, [roleId, isCreateMode, userRoleService, loadPermissions, setAllPermissions]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(prefixKey('currentEditingRoleId'));
      window.dispatchEvent(new Event('permissionsUpdated'));
    };
  }, []);

  return {
    roleName,
    setRoleName,
    roleDescription,
    setRoleDescription,
    isSystem,
    isActive,
    setIsActive,
    users,
    setUsers,
    allRoles,
    accessDenied,
    initialSnapshotRef,
  };
};
